export type ShopifyPeriod = "today" | "7d" | "this_month" | "last_month" | "3m" | "custom"

const DEFAULT_TIMEZONE = "UTC"

function pad2(value: number): string {
  return String(value).padStart(2, "0")
}

/**
 * Strict date-only parser for inputs intended for a Postgres `date` column.
 *
 * Why not Date.parse:
 *   - Date.parse("2026-04-01T00:00:00+03:00") returns a timestamp that, when
 *     converted via toISOString().slice(0,10), yields "2026-03-31" — the user
 *     meant April 1 but we'd save March 31 (a UTC-conversion shift).
 *   - Date.parse("2026-02-30") succeeds: JS Date silently rolls over to March 2.
 *     An impossible date becomes a different valid date.
 *
 * Contract:
 *   - Accepts "YYYY-MM-DD" or any "YYYY-MM-DDTrest" prefix (timestamp form).
 *   - Always preserves the LITERAL leading calendar date (no UTC conversion).
 *   - Rejects rollovers (2026-02-30, 2026-13-01, etc.) by round-tripping
 *     through Date.UTC and confirming Y/M/D match.
 *   - Returns null for unparseable / invalid input.
 */
const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/

export function normalizeDateForDB(input: string | null | undefined): string | null {
  if (input == null) return null
  if (typeof input !== "string") return null
  const m = DATE_PREFIX_PATTERN.exec(input)
  if (!m) return null
  const y = parseInt(m[1], 10)
  const mo = parseInt(m[2], 10)
  const d = parseInt(m[3], 10)
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null

  // Real-calendar check via round-trip: Date.UTC silently rolls bad dates
  // forward, but the round-trip mismatch surfaces them.
  const utcMs = Date.UTC(y, mo - 1, d)
  const checked = new Date(utcMs)
  if (
    checked.getUTCFullYear() !== y ||
    checked.getUTCMonth() !== mo - 1 ||
    checked.getUTCDate() !== d
  ) {
    return null
  }

  // Format from the captured groups (already zero-padded by regex).
  return `${m[1]}-${m[2]}-${m[3]}`
}

type DateTimeParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function getTimeZoneFormattedParts(date: Date, timezone: string): DateTimeParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })

  const parts = formatter.formatToParts(date)
  const result: Record<string, number> = {}

  for (const part of parts) {
    if (part.type !== "literal") {
      result[part.type] = Number.parseInt(part.value, 10)
    }
  }

  return {
    year: result.year,
    month: result.month,
    day: result.day,
    hour: result.hour,
    minute: result.minute,
    second: result.second,
  }
}

function utcToZonedTime(date: Date, timezone: string): Date {
  const parts = getTimeZoneFormattedParts(date, timezone)
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second))
}

function getTimeZoneOffset(date: Date, timezone: string): number {
  const parts = getTimeZoneFormattedParts(date, timezone)
  const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
  return asUTC - date.getTime()
}

function parseLocalISOString(isoString: string): DateTimeParts {
  const [datePart, timePart = "00:00:00"] = isoString.split("T")
  const [yearStr, monthStr, dayStr] = datePart.split("-")
  const [hourStr = "0", minuteStr = "0", secondStr = "0"] = timePart.split(":")

  return {
    year: Number.parseInt(yearStr, 10),
    month: Number.parseInt(monthStr, 10),
    day: Number.parseInt(dayStr, 10),
    hour: Number.parseInt(hourStr, 10),
    minute: Number.parseInt(minuteStr, 10),
    second: Number.parseInt(secondStr, 10),
  }
}

export function zonedTimeToUtc(localISOString: string, timezone: string): Date {
  const { year, month, day, hour, minute, second } = parseLocalISOString(localISOString)
  const initialGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  const initialOffset = getTimeZoneOffset(initialGuess, timezone)

  let utcTime = initialGuess.getTime() - initialOffset
  const adjustedDate = new Date(utcTime)
  const adjustedOffset = getTimeZoneOffset(adjustedDate, timezone)

  if (adjustedOffset !== initialOffset) {
    utcTime = initialGuess.getTime() - adjustedOffset
  }

  return new Date(utcTime)
}

function parseMonthKey(monthKey: string): { year: number; month: number } {
  const [yearPart, monthPart] = monthKey.split("-")
  const year = Number.parseInt(yearPart, 10)
  const month = Number.parseInt(monthPart, 10)

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    throw new Error(`Invalid month key: ${monthKey}`)
  }

  return { year, month }
}

function addOneMonth(year: number, month: number): { year: number; month: number } {
  if (month === 12) {
    return { year: year + 1, month: 1 }
  }
  return { year, month: month + 1 }
}

function zonedMidnightIso(year: number, month: number): string {
  return `${year}-${pad2(month)}-01T00:00:00`
}

export function getMonthKeyForTimezone(date: Date, timezone: string = DEFAULT_TIMEZONE): string {
  const zonedDate = utcToZonedTime(date, timezone)
  const year = zonedDate.getUTCFullYear()
  const month = zonedDate.getUTCMonth() + 1
  return `${year}-${pad2(month)}`
}

export function monthKeyToUtcRange(
  monthKey: string,
  timezone: string = DEFAULT_TIMEZONE,
): { month_start_utc: Date; month_end_utc: Date } {
  const { year, month } = parseMonthKey(monthKey)
  const monthStartUtc = zonedTimeToUtc(zonedMidnightIso(year, month), timezone)
  const nextMonth = addOneMonth(year, month)
  const monthEndUtc = zonedTimeToUtc(zonedMidnightIso(nextMonth.year, nextMonth.month), timezone)

  return {
    month_start_utc: monthStartUtc,
    month_end_utc: monthEndUtc,
  }
}

export function monthKeyRangeToUtcRange(
  startMonthKey: string,
  endMonthKey: string,
  timezone: string = DEFAULT_TIMEZONE,
): { month_start_utc: Date; month_end_utc: Date } {
  const startBounds = monthKeyToUtcRange(startMonthKey, timezone)
  const endBounds = monthKeyToUtcRange(endMonthKey, timezone)

  return {
    month_start_utc: startBounds.month_start_utc,
    month_end_utc: endBounds.month_end_utc,
  }
}

export function getMonthKeysInRange(
  month_start_utc: Date,
  month_end_utc: Date,
  timezone: string = DEFAULT_TIMEZONE,
): string[] {
  const keys: string[] = []
  const startKey = getMonthKeyForTimezone(month_start_utc, timezone)
  const endKey = getMonthKeyForTimezone(new Date(month_end_utc.getTime() - 1), timezone)

  let { year, month } = parseMonthKey(startKey)
  const { year: endYear, month: endMonth } = parseMonthKey(endKey)

  // Guard against an invalid range where the start month comes after the end month.
  const startsAfterEnd =
    year > endYear || (year === endYear && month > endMonth)
  if (startsAfterEnd) {
    return keys
  }

  while (year < endYear || (year === endYear && month <= endMonth)) {
    keys.push(`${year}-${pad2(month)}`)
    const next = addOneMonth(year, month)
    year = next.year
    month = next.month
  }

  return keys
}

export function isValidExpensePeriod(
  month_start_utc: Date,
  month_end_utc: Date,
  timezone: string = DEFAULT_TIMEZONE,
): boolean {
  const expectedKey = getMonthKeyForTimezone(month_start_utc, timezone)
  const expectedBounds = monthKeyToUtcRange(expectedKey, timezone)

  return (
    month_start_utc.getTime() === expectedBounds.month_start_utc.getTime() &&
    month_end_utc.getTime() === expectedBounds.month_end_utc.getTime()
  )
}

/**
 * Returns true when the entire requested range falls within one calendar month in the given timezone.
 * `month_end_utc` is treated as exclusive.
 */
export function isSingleMonthRange(
  month_start_utc: Date,
  month_end_utc: Date,
  timezone: string = DEFAULT_TIMEZONE,
): boolean {
  if (month_end_utc.getTime() <= month_start_utc.getTime()) {
    return false
  }

  const startMonthKey = getMonthKeyForTimezone(month_start_utc, timezone)
  const endInclusive = new Date(month_end_utc.getTime() - 1)
  const endMonthKey = getMonthKeyForTimezone(endInclusive, timezone)

  return startMonthKey === endMonthKey
}

export function parseSearchParamsToMonthKey(
  searchParams: URLSearchParams,
  timezone: string = DEFAULT_TIMEZONE,
): string {
  const startParam = searchParams.get("month_start_utc") ?? searchParams.get("start_at")
  const endParam = searchParams.get("month_end_utc") ?? searchParams.get("end_at")

  if (startParam && endParam) {
    const start = new Date(startParam)
    const end = new Date(endParam)

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      if (isValidExpensePeriod(start, end, timezone)) {
        return getMonthKeyForTimezone(start, timezone)
      }
    }
  }

  return getMonthKeyForTimezone(new Date(), timezone)
}

export function formatDateRange(
  month_start_utc: Date,
  month_end_utc: Date,
  timezone: string = DEFAULT_TIMEZONE,
): string {
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: timezone,
  })

  const start = formatter.format(month_start_utc)
  const endExclusive = new Date(month_end_utc.getTime() - 1)
  const end = formatter.format(endExclusive)

  return `${start} - ${end}`
}

export function formatMonthKey(monthKey: string, timezone: string = DEFAULT_TIMEZONE): string {
  const { month_start_utc } = monthKeyToUtcRange(monthKey, timezone)

  return month_start_utc.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    timeZone: timezone,
  })
}

/**
 * Format a Date to YYYY-MM-DD in the given timezone.
 */
export function toDateKeyTz(date: Date, timezone: string = DEFAULT_TIMEZONE): string {
  const p = getTimeZoneFormattedParts(date, timezone)
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`
}

/**
 * Convert a UTC ISO string (e.g. from DB) to YYYY-MM-DD in the given timezone.
 */
export function utcToDateKeyTz(isoStr: string, timezone: string = DEFAULT_TIMEZONE): string {
  return toDateKeyTz(new Date(isoStr), timezone)
}

/**
 * Get today's date in YYYY-MM-DD format for a given timezone.
 */
export function getTodayInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  return toDateKeyTz(new Date(), timezone)
}

/**
 * Convert a YYYY-MM-DD date string to an ISO timestamp at start of day in the given timezone.
 * Returns the UTC ISO string.
 */
export function dateToTimezoneISO(dateStr: string, timezone: string = DEFAULT_TIMEZONE): string {
  const utcDate = zonedTimeToUtc(`${dateStr}T00:00:00`, timezone)
  return utcDate.toISOString()
}

/**
 * Resolve a user's IANA timezone for date math, falling back to the default
 * (UTC) when the profile value is missing or blank. Single source for the
 * `user.timezone || UTC` guard that the Economics page, the bundle backtest,
 * and the constructor all need.
 */
export function resolveUserTimezone(
  user: { timezone?: string | null } | null | undefined,
): string {
  const tz = user?.timezone
  return typeof tz === "string" && tz.trim() !== "" ? tz : DEFAULT_TIMEZONE
}

/**
 * Convert a YYYY-MM-DD day range to UTC Date boundaries.
 * `end` is exclusive: start of the day AFTER `endYMD`.
 */
export function dayRangeToUtcRange(
  startYMD: string,
  endYMD: string,
  timezone: string = DEFAULT_TIMEZONE,
): { month_start_utc: Date; month_end_utc: Date } {
  const month_start_utc = zonedTimeToUtc(`${startYMD}T00:00:00`, timezone)

  // end is exclusive — add one day
  const [yStr, mStr, dStr] = endYMD.split("-")
  const endDate = new Date(
    Number.parseInt(yStr, 10),
    Number.parseInt(mStr, 10) - 1,
    Number.parseInt(dStr, 10) + 1,
  )
  const nextDayYMD = `${endDate.getFullYear()}-${pad2(endDate.getMonth() + 1)}-${pad2(endDate.getDate())}`
  const month_end_utc = zonedTimeToUtc(`${nextDayYMD}T00:00:00`, timezone)

  return { month_start_utc, month_end_utc }
}

/**
 * Compute a UTC date range for a given Shopify period preset.
 */
export function shopifyPeriodToDateRange(
  period: ShopifyPeriod,
  timezone: string = DEFAULT_TIMEZONE,
  customRange?: { from: string; to: string },
): { month_start_utc: Date; month_end_utc: Date } {
  const now = new Date()
  const todayKey = toDateKeyTz(now, timezone)
  const currentMonthKey = getMonthKeyForTimezone(now, timezone)

  switch (period) {
    case "today":
      return dayRangeToUtcRange(todayKey, todayKey, timezone)

    case "7d": {
      const parts = getTimeZoneFormattedParts(now, timezone)
      const localToday = new Date(parts.year, parts.month - 1, parts.day)
      localToday.setDate(localToday.getDate() - 6)
      const startYMD = `${localToday.getFullYear()}-${pad2(localToday.getMonth() + 1)}-${pad2(localToday.getDate())}`
      return dayRangeToUtcRange(startYMD, todayKey, timezone)
    }

    case "this_month":
      return monthKeyToUtcRange(currentMonthKey, timezone)

    case "last_month": {
      const { year, month } = parseMonthKey(currentMonthKey)
      const prev = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 }
      const prevKey = `${prev.year}-${pad2(prev.month)}`
      return monthKeyToUtcRange(prevKey, timezone)
    }

    case "3m": {
      const { year, month } = parseMonthKey(currentMonthKey)
      let startMonth = month - 2
      let startYear = year
      while (startMonth < 1) {
        startMonth += 12
        startYear -= 1
      }
      const startKey = `${startYear}-${pad2(startMonth)}`
      return monthKeyRangeToUtcRange(startKey, currentMonthKey, timezone)
    }

    case "custom": {
      if (!customRange) {
        return monthKeyToUtcRange(currentMonthKey, timezone)
      }
      return dayRangeToUtcRange(customRange.from, customRange.to, timezone)
    }
  }
}


export function startOfUtcDay(date: Date): string {
  const d = new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}

export function startOfUtcMonth(date: Date): string {
  const d = new Date(date)
  d.setUTCDate(1)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}
